import { Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';

import styles from './LocationsListing.module.scss';

export type DateRangeProps = {
  name: string;
  fields: DateRangeFields;
  active: boolean;
};

export type DateRangeFields = {
  dateRangeStart: Field<Date>;
  dateRangeEnd: Field<Date>;
  mondayOpen: Field<string>;
  mondayClose: Field<string>;
  tuesdayOpen: Field<string>;
  tuesdayClose: Field<string>;
  wednesdayOpen: Field<string>;
  wednesdayClose: Field<string>;
  thursdayOpen: Field<string>;
  thursdayClose: Field<string>;
  fridayOpen: Field<string>;
  fridayClose: Field<string>;
  saturdayOpen: Field<string>;
  saturdayClose: Field<string>;
  sundayOpen: Field<string>;
  sundayClose: Field<string>;
};

type DateRangeObject = {
  label: string;
  open: string;
  close: string;
};

const cx = classNames.bind(styles);

const DateRange = (props: DateRangeProps): JSX.Element => {
  const range = props.fields;
  const [dateLabel, setDateLabel] = useState<Field<string>>();

  function formatTime(timeOpen: string, timeClose: string) {
    const open = getTime(timeOpen, true);
    const close = getTime(timeClose, false);
    const timeSpan: Field<string> = {
      value: open + ' - ' + close,
    };
    if (open === '' || close === '') {
      timeSpan.value = 'CLOSED';
    }
    return timeSpan;
  }

  function getTime(time: string, start: boolean) {
    try {
      const splitTime = time.split(':');
      let hour = Number(splitTime[0]);
      const minute = splitTime[1];
      let amPm = 'am';
      if (isNaN(hour) || isNaN(Number(splitTime[1]))) {
        return '';
      }
      if (hour > 12) {
        amPm = 'pm';
        hour = hour - 12;
      }
      if (hour == 12 && start) {
        return 'Noon';
      } else if (hour == 12 && !start) {
        return 'noon';
      } else {
        return hour.toString() + ':' + minute.toString() + amPm;
      }
    } catch {
      return '';
    }
  }

  function formatDates(passStart: Field<Date>, passEnd: Field<Date>) {
    const tempStart = new Date(passStart.value);
    const tempEnd = new Date(passEnd.value);
    let tempLabel = '';

    /* If start and end date both have the same month and day, then it covers the full year and we do not display date */
    if (tempStart.getMonth() == tempEnd.getMonth() && tempStart.getDate() === tempEnd.getDate()) {
      setDateLabel({ value: '' });
      return;
    }

    /* Dates with years of 0 are not set. If both start and end are not set, it covers the full year and we do not display dates */
    if (tempStart.getFullYear() == 0 && tempEnd.getFullYear() == 0) {
      setDateLabel({ value: '' });
      return;
    }

    if (tempStart.getFullYear() == 0) {
      tempLabel += 'Now through ';
    } else {
      tempLabel += tempStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) + ' - ';
    }

    if (tempEnd.getFullYear() == 0) {
      tempLabel = 'Beginning ' + tempStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    } else {
      tempLabel += tempEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    }

    setDateLabel({ value: tempLabel });
  }

  const dateRanges: DateRangeObject[] = [
    { label: 'Mon.', open: range.mondayOpen.value, close: range.mondayClose.value },
    { label: 'Tues.', open: range.tuesdayOpen.value, close: range.tuesdayClose.value },
    { label: 'Weds.', open: range.wednesdayOpen.value, close: range.wednesdayClose.value },
    { label: 'Thurs.', open: range.thursdayOpen.value, close: range.thursdayClose.value },
    { label: 'Fri.', open: range.fridayOpen.value, close: range.fridayClose.value },
    { label: 'Sat.', open: range.saturdayOpen.value, close: range.saturdayClose.value },
    { label: 'Sun.', open: range.sundayOpen.value, close: range.sundayClose.value },
  ];

  function getFormattedTimeElement(obj: DateRangeObject, index: number): JSX.Element {
    const { label, open, close } = obj;

    return (
      <tr key={`${label}-${index}`}>
        <td>{label}</td>
        <td>
          <Text field={formatTime(open, close)} />
        </td>
      </tr>
    );
  }

  useEffect(() => {
    if (dateLabel === undefined) {
      formatDates(range.dateRangeStart, range.dateRangeEnd);
    }
  });
  return (
    <table className={cx({ daysAndTimes: true, inactive: !props.active })}>
      <thead>
        <tr>
          <th className={styles['dateSpan']} colSpan={2}>
            <Text field={dateLabel} />
          </th>
        </tr>
      </thead>
      <tbody>{dateRanges.map((obj: DateRangeObject, index) => getFormattedTimeElement(obj, index))}</tbody>
    </table>
  );
};

export default DateRange;
